import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  User,
  Calendar,
  Clock,
  FileText,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  Umbrella,
  CreditCard,
  CalendarDays,
  Zap,
  Settings2,
  Bell,
  Activity,
  Fuel,
  CalendarOff,
} from "lucide-react";
import { AuthContext } from "../../Moduels/Context/AuthContext";
import {
  fetchMyAttendance,
  fetchMyOrganisationSummary,
} from "../../Moduels/attendance/attendanceApi";
import { mapAttendanceRow } from "../../Moduels/attendance/attendanceService";
import { fetchLeaveBalances, fetchLeaveRequests } from "../../Moduels/leave/leaveApi";
import { fetchMyPayslipRunOptions } from "../../Moduels/Payslip/payslipApi";
import { fetchWfHolidays } from "../../Moduels/attendance/wfApi";
import { fetchNotices } from "../../services/noticeApi";
import {
  resolveEmployeeIdFromProfile,
  buildSelfEmployeeFromProfile,
} from "../common/employeeUtils";
import { EMPLOYEE_WORKSPACE_ROUTES } from "../../config/employeeWorkspaceNav";
import { EmployeeFuelHomeCard } from "../../Moduels/AllowancesBenefits/EmployeeFuelRequestPage";
import "../../styles/employee-dashboard.css";

// ─── helpers ────────────────────────────────────────────────────────────────

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

function formatDate(d = new Date()) {
  return d.toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Local calendar date (avoid UTC shift from toISOString). */
function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseDateOnly(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function daysAgoISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function upcomingFrom(list = [], limit = 5) {
  const today = todayISO();
  return list
    .filter((h) => (h.holiday_date || h.date || "") >= today)
    .sort((a, b) =>
      (a.holiday_date || a.date || "").localeCompare(b.holiday_date || b.date || "")
    )
    .slice(0, limit);
}

function formatHolidayDate(dateStr) {
  const d = parseDateOnly(dateStr);
  if (!d) return { day: "", month: "" };
  return {
    day: d.getDate(),
    month: d.toLocaleString("en-IN", { month: "short" }).toUpperCase(),
  };
}

function formatHolidayWeekday(dateStr) {
  const d = parseDateOnly(dateStr);
  if (!d) return "";
  return d.toLocaleDateString("en-IN", { weekday: "long" });
}

function resolveDisplayFirstName(user) {
  if (!user) return "there";
  const self = buildSelfEmployeeFromProfile(user);
  if (self?.first_name) return self.first_name;
  if (self?.display_name) return self.display_name.split(" ")[0];
  if (user.employee) {
    const parts = [user.employee.first_name, user.employee.last_name].filter(Boolean);
    if (parts.length) return parts[0];
    if (user.employee.email) return user.employee.email.split("@")[0];
  }
  if (user.user?.full_name) return user.user.full_name.split(" ")[0];
  if (user.user?.username) return user.user.username;
  if (user.full_name) return String(user.full_name).split(" ")[0];
  if (user.first_name) return user.first_name;
  if (user.username) return user.username;
  if (user.name) return String(user.name).split(" ")[0];
  return "there";
}

function normalizeLeaveBalance(row) {
  const allocated =
    Number(row?.allocated ?? row?.entitled ?? row?.opening_balance ?? 0) || 0;
  const used = Number(row?.used ?? row?.consumed ?? 0) || 0;
  const available =
    row?.available != null
      ? Number(row.available)
      : row?.balance != null
        ? Number(row.balance)
        : row?.remaining_days != null
          ? Number(row.remaining_days)
          : Math.max(0, allocated - used);
  return {
    allocated,
    used,
    available: Number.isFinite(available) ? available : null,
    typeName: row?.leave_type_name || row?.leave_type_code || row?.leave_type || "Leave",
  };
}

function payslipStatusBadge(opt) {
  if (!opt) return null;
  if (opt.payslip_available === false) return "Pending finalization";
  if (opt.lifecycle_status === "locked") return "Paid";
  if (opt.status === "processed") return "Processed";
  if (opt.status) return String(opt.status).replace(/_/g, " ");
  return null;
}

function noticePriorityLabel(priority) {
  const p = String(priority || "normal").toLowerCase();
  if (p === "urgent") return "Urgent";
  if (p === "important") return "Important";
  return "Normal";
}

function noticeTitle(text = "") {
  const trimmed = String(text).trim();
  if (!trimmed) return "Notice";
  const firstLine = trimmed.split(/\n/)[0].trim();
  return firstLine.length > 72 ? `${firstLine.slice(0, 72)}…` : firstLine;
}

function noticePreview(text = "") {
  const trimmed = String(text).trim();
  if (!trimmed) return "";
  return trimmed.length > 80 ? `${trimmed.slice(0, 80)}…` : trimmed;
}

// ─── sub-components ─────────────────────────────────────────────────────────

function KpiCard({ icon, iconBg, label, value, subLabel, badge, link, linkLabel, onLink }) {
  return (
    <div className="ed-kpi-card">
      <div className="ed-kpi-top">
        <div className="ed-kpi-icon" style={{ background: iconBg }}>
          {icon}
        </div>
        <span className="ed-kpi-label">{label}</span>
      </div>
      <div className="ed-kpi-value">{value}</div>
      {badge ? <span className="ed-kpi-badge">{badge}</span> : null}
      {subLabel ? <div className="ed-kpi-sub">{subLabel}</div> : null}
      {link ? (
        <button className="ed-kpi-link" onClick={onLink} type="button">
          {linkLabel} <ChevronRight size={13} />
        </button>
      ) : null}
    </div>
  );
}

function QuickActionBtn({ icon, label, desc, color, onClick }) {
  return (
    <button className="ed-qa-btn" style={{ "--qa-color": color }} onClick={onClick} type="button">
      <span className="ed-qa-icon">{icon}</span>
      <span className="ed-qa-title">{label}</span>
      <span className="ed-qa-desc">{desc}</span>
    </button>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function EmployeeDashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const firstName = resolveDisplayFirstName(user);
  const employeeId = resolveEmployeeIdFromProfile(user);
  const today = todayISO();
  const year = new Date().getFullYear();

  // ── organisation (same source as Leave module) ──────────────────────────
  const { data: orgSummary } = useQuery({
    queryKey: ["organisation-me"],
    queryFn: fetchMyOrganisationSummary,
    staleTime: 5 * 60_000,
  });
  const orgId = orgSummary?.id || orgSummary?.organisation_id || user?.organisation_id || "";

  // ── today attendance ────────────────────────────────────────────────────
  const { data: attendanceRows = [] } = useQuery({
    queryKey: ["my-attendance-today", today],
    queryFn: () => fetchMyAttendance({ from_date: today, to_date: today }),
    staleTime: 60_000,
  });

  const todayMapped = useMemo(() => {
    const mapped = (Array.isArray(attendanceRows) ? attendanceRows : [])
      .map(mapAttendanceRow)
      .filter((r) => r.date);
    return mapped.find((r) => r.date === today) || null;
  }, [attendanceRows, today]);

  const attendanceDisplay = useMemo(() => {
    // mapAttendanceRow normalizes to dashboard words (present, half-day, …)
    if (!todayMapped?.status) return { label: "Not Marked", color: "var(--slate-400)" };
    const s = String(todayMapped.status).toLowerCase();
    const map = {
      present: { label: "Present", color: "var(--green-600)" },
      absent: { label: "Absent", color: "var(--red-500)" },
      "half-day": { label: "Half Day", color: "var(--orange-500)" },
      half_day: { label: "Half Day", color: "var(--orange-500)" },
      leave: { label: "On Leave", color: "var(--purple-600)" },
      "week-off": { label: "Week Off", color: "var(--slate-500)" },
      week_off: { label: "Week Off", color: "var(--slate-500)" },
      holiday: { label: "Holiday", color: "var(--blue-600)" },
    };
    return map[s] || { label: String(todayMapped.status), color: "var(--slate-500)" };
  }, [todayMapped]);

  // ── leave balance ───────────────────────────────────────────────────────
  const { data: leaveBalancesRaw } = useQuery({
    queryKey: ["leave-balance-me", orgId, employeeId, year],
    queryFn: () =>
      fetchLeaveBalances({ organisation_id: orgId, employee_id: employeeId, year }),
    enabled: Boolean(orgId && employeeId),
    staleTime: 120_000,
  });

  const leaveBalances = useMemo(() => {
    if (Array.isArray(leaveBalancesRaw)) return leaveBalancesRaw;
    return leaveBalancesRaw?.balances || [];
  }, [leaveBalancesRaw]);

  const totalLeaveBalance = useMemo(() => {
    if (!leaveBalances.length) return { available: null, count: 0 };
    let total = 0;
    let count = 0;
    for (const row of leaveBalances) {
      const norm = normalizeLeaveBalance(row);
      if (norm.available != null) {
        total += norm.available;
        count++;
      }
    }
    return { available: count > 0 ? total : null, count };
  }, [leaveBalances]);
  const hasLeaveData = leaveBalances.length > 0 && totalLeaveBalance.available != null;

  // ── payslip ─────────────────────────────────────────────────────────────
  const { data: payslipRuns = [] } = useQuery({
    queryKey: ["my-payslip-runs"],
    queryFn: fetchMyPayslipRunOptions,
    staleTime: 300_000,
    retry: 1,
  });
  const latestRun = Array.isArray(payslipRuns) && payslipRuns.length ? payslipRuns[0] : null;
  const payslipPeriod = latestRun?.month_label || latestRun?.period_label || "—";
  const payslipBadge = payslipStatusBadge(latestRun);

  // ── holidays ────────────────────────────────────────────────────────────
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const { data: holidays = [] } = useQuery({
    queryKey: ["wf-holidays", year],
    queryFn: () => fetchWfHolidays({ from_date: yearStart, to_date: yearEnd }),
    staleTime: 600_000,
  });
  const upcoming = upcomingFrom(Array.isArray(holidays) ? holidays : []);
  const nextHoliday = upcoming[0];
  const nextHolidayDate = nextHoliday?.holiday_date || nextHoliday?.date;
  const nextHolidayName = nextHoliday?.name || nextHoliday?.holiday_name || "—";

  // ── announcements (Notice Board) ────────────────────────────────────────
  const { data: notices = [] } = useQuery({
    queryKey: ["org-notices"],
    queryFn: fetchNotices,
    staleTime: 120_000,
    retry: 1,
  });
  const announcements = useMemo(() => (Array.isArray(notices) ? notices : []).slice(0, 5), [notices]);

  // ── recent activity (my leave + recent attendance) ──────────────────────
  const activityFrom = daysAgoISO(30);
  const { data: myLeaveRequests = [] } = useQuery({
    queryKey: ["my-leave-requests", orgId, employeeId],
    queryFn: () =>
      fetchLeaveRequests({ organisation_id: orgId, employee_id: employeeId }),
    enabled: Boolean(orgId && employeeId),
    staleTime: 60_000,
    retry: 1,
  });
  const { data: recentAttendanceRaw = [] } = useQuery({
    queryKey: ["my-attendance-recent", activityFrom, today],
    queryFn: () => fetchMyAttendance({ from_date: activityFrom, to_date: today }),
    staleTime: 60_000,
    retry: 1,
  });

  const recentActivity = useMemo(() => {
    const leaveRows = Array.isArray(myLeaveRequests)
      ? myLeaveRequests
      : myLeaveRequests?.items || [];

    const leaveItems = [...leaveRows]
      .sort((a, b) =>
        String(b.applied_at || b.created_at || "").localeCompare(
          String(a.applied_at || a.created_at || "")
        )
      )
      .slice(0, 4)
      .map((req) => {
        const status = String(req.status || "").toLowerCase();
        const start = req.start_date || req.from_date;
        return {
          id: `leave-${req.request_id || req.id}`,
          icon: "leave",
          title:
            status === "approved"
              ? "Leave application approved"
              : status === "rejected"
                ? "Leave application rejected"
                : status === "cancelled"
                  ? "Leave application cancelled"
                  : "Leave application submitted",
          sub: req.leave_type_name
            ? `${req.leave_type_name}${start ? ` · ${start}` : ""}`
            : start || "Leave request",
          time: req.applied_at || req.created_at,
          status,
        };
      });

    const attItems = (Array.isArray(recentAttendanceRaw) ? recentAttendanceRaw : [])
      .map(mapAttendanceRow)
      .filter((r) => r.date && (r.checkIn || r.status))
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 4)
      .map((r) => ({
        id: `att-${r.date}`,
        icon: "attendance",
        title: r.checkIn ? "Attendance marked" : `Attendance: ${r.status || "updated"}`,
        sub: r.checkIn ? `Check-in at ${r.checkIn}` : r.date,
        time: r.date,
        status: "present",
      }));

    return [...leaveItems, ...attItems]
      .sort((a, b) => String(b.time || "").localeCompare(String(a.time || "")))
      .slice(0, 6);
  }, [myLeaveRequests, recentAttendanceRaw]);

  // ─── render ─────────────────────────────────────────────────────────────

  const quickActions = [
    {
      icon: <FileText size={28} />,
      label: "View Payslip",
      desc: "Download your payslip",
      color: "var(--indigo-500)",
      onClick: () => navigate(EMPLOYEE_WORKSPACE_ROUTES.payslip),
    },
    {
      icon: <CalendarDays size={28} />,
      label: "Apply Leave",
      desc: "Submit a leave request",
      color: "var(--green-600)",
      onClick: () => navigate(EMPLOYEE_WORKSPACE_ROUTES.leave),
    },
    {
      icon: <CalendarOff size={28} />,
      label: "Comp Off",
      desc: "Balance and apply credit",
      color: "var(--orange-500)",
      onClick: () => navigate(EMPLOYEE_WORKSPACE_ROUTES.compOff),
    },
    {
      icon: <Download size={28} />,
      label: "Download Documents",
      desc: "Download important docs",
      color: "var(--purple-600)",
      onClick: () => navigate("/form16"),
    },
    {
      icon: <Activity size={28} />,
      label: "My Attendance",
      desc: "View your attendance",
      color: "var(--orange-500)",
      onClick: () => navigate(EMPLOYEE_WORKSPACE_ROUTES.attendance),
    },
    {
      icon: <Fuel size={28} />,
      label: "Fuel Bill",
      desc: "Raise or track a bill",
      color: "var(--orange-500)",
      onClick: () => navigate(EMPLOYEE_WORKSPACE_ROUTES.fuel),
    },
    {
      icon: <Settings2 size={28} />,
      label: "More",
      desc: "View all options",
      color: "var(--slate-500)",
      onClick: () => navigate("/profile"),
    },
  ];

  function activityIcon(item) {
    if (item.icon === "leave") {
      if (item.status === "approved") return <CheckCircle size={16} color="var(--green-600)" />;
      if (item.status === "rejected") return <XCircle size={16} color="var(--red-500)" />;
      return <AlertCircle size={16} color="var(--orange-500)" />;
    }
    if (item.icon === "attendance") return <Clock size={16} color="var(--blue-600)" />;
    return <Bell size={16} color="var(--slate-400)" />;
  }

  function formatActivityTime(timeStr) {
    if (!timeStr) return "";
    const asDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(timeStr).slice(0, 10))
      && String(timeStr).length <= 10;
    const d = asDateOnly ? parseDateOnly(timeStr) : new Date(timeStr);
    if (!d || Number.isNaN(d.getTime())) return String(timeStr);
    const diff = Date.now() - d.getTime();
    const mins = Math.round(diff / 60000);
    if (mins >= 0 && mins < 60) return `${Math.max(mins, 1)}m ago`;
    if (mins >= 0 && mins < 1440) {
      return `Today, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
    }
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  }

  return (
    <div className="ed-root">
      {/* ── Header ── */}
      <div className="ed-header">
        <div>
          <h1 className="ed-greeting">
            {getGreeting()}, {firstName}
          </h1>
          <div className="ed-date">
            <CalendarDays size={14} />
            {formatDate()}
          </div>
        </div>
        <button
          className="ed-profile-btn"
          type="button"
          onClick={() => navigate("/profile")}
        >
          <User size={16} />
          View Full Profile
        </button>
      </div>

      {/* ── KPI Cards ── */}
      <div className="ed-kpi-grid">
        <KpiCard
          icon={<Clock size={22} color="var(--blue-600)" />}
          iconBg="var(--blue-50)"
          label="Attendance Today"
          value={
            <span style={{ color: attendanceDisplay.color }}>{attendanceDisplay.label}</span>
          }
          subLabel={todayMapped?.checkIn ? `Check-in ${todayMapped.checkIn}` : null}
          link
          linkLabel="View Attendance"
          onLink={() => navigate(EMPLOYEE_WORKSPACE_ROUTES.attendance)}
        />
        <KpiCard
          icon={<CalendarDays size={22} color="var(--green-600)" />}
          iconBg="var(--green-50)"
          label="Leave Balance"
          value={
            <span style={{ color: "var(--green-600)" }}>
              {hasLeaveData ? `${totalLeaveBalance.available} Days` : "—"}
            </span>
          }
          subLabel={hasLeaveData ? `Total Balance (${totalLeaveBalance.count} types)` : "No leave allocated"}
          link
          linkLabel="Apply Leave"
          onLink={() => navigate(EMPLOYEE_WORKSPACE_ROUTES.leave)}
        />
        <KpiCard
          icon={<CreditCard size={22} color="var(--purple-600)" />}
          iconBg="var(--purple-50)"
          label="Payslip Status"
          value={
            <span style={{ color: "var(--purple-600)" }}>{payslipPeriod}</span>
          }
          badge={payslipBadge}
          link
          linkLabel="View Payslip"
          onLink={() => navigate(EMPLOYEE_WORKSPACE_ROUTES.payslip)}
        />
        <KpiCard
          icon={<Umbrella size={22} color="var(--orange-500)" />}
          iconBg="var(--orange-50)"
          label="Next Holiday"
          value={
            <span style={{ color: "var(--orange-500)" }}>
              {nextHolidayDate
                ? parseDateOnly(nextHolidayDate)?.toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "—"}
            </span>
          }
          subLabel={nextHolidayName}
          link
          linkLabel="View Calendar"
          onLink={() => navigate(EMPLOYEE_WORKSPACE_ROUTES.holidays)}
        />
      </div>

      {/* ── Quick Actions ── */}
      <div className="ed-section-card">
        <div className="ed-section-header">
          <span className="ed-section-title">
            <Zap size={17} color="var(--indigo-500)" /> Quick Actions
          </span>
          <button
            className="ed-text-btn"
            type="button"
            onClick={() => navigate("/profile")}
          >
            <Settings2 size={14} /> Customize
          </button>
        </div>
        <div className="ed-qa-grid">
          {quickActions.map((qa) => (
            <QuickActionBtn key={qa.label} {...qa} />
          ))}
        </div>
      </div>

      <EmployeeFuelHomeCard />

      {/* ── Bottom 3-col ── */}
      <div className="ed-bottom-grid">
        {/* Recent Activity */}
        <div className="ed-section-card">
          <div className="ed-section-header">
            <span className="ed-section-title">
              <Activity size={16} color="var(--indigo-500)" /> Recent Activity
            </span>
            <button
              className="ed-text-btn"
              type="button"
              onClick={() => navigate(EMPLOYEE_WORKSPACE_ROUTES.leave)}
            >
              View All
            </button>
          </div>
          {recentActivity.length === 0 ? (
            <div className="ed-empty">No recent activity</div>
          ) : (
            <ul className="ed-activity-list">
              {recentActivity.map((item) => (
                <li key={item.id} className="ed-activity-item">
                  <span className="ed-activity-icon">{activityIcon(item)}</span>
                  <div className="ed-activity-body">
                    <div className="ed-activity-title">{item.title}</div>
                    <div className="ed-activity-sub">{item.sub}</div>
                  </div>
                  <span className="ed-activity-time">{formatActivityTime(item.time)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Announcements */}
        <div className="ed-section-card">
          <div className="ed-section-header">
            <span className="ed-section-title">
              <Bell size={16} color="var(--indigo-500)" /> Announcements
            </span>
            <button
              className="ed-text-btn"
              type="button"
              onClick={() => navigate("/noticeboard")}
            >
              View All
            </button>
          </div>
          {announcements.length === 0 ? (
            <div className="ed-empty">No announcements</div>
          ) : (
            <ul className="ed-announce-list">
              {announcements.map((a, i) => (
                <li key={a.id || i} className="ed-announce-item">
                  <div className="ed-announce-row">
                    <span className="ed-announce-emoji">📢</span>
                    <div className="ed-announce-body">
                      <div className="ed-announce-title">{noticeTitle(a.text)}</div>
                      <div className="ed-announce-sub">
                        {noticePriorityLabel(a.priority)}
                        {a.category ? ` · ${a.category}` : ""}
                        {a.text && a.text.length > 72 ? ` · ${noticePreview(a.text)}` : ""}
                      </div>
                    </div>
                    <span className="ed-announce-date">
                      {a.created_at
                        ? new Date(a.created_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : ""}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Upcoming Holidays */}
        <div className="ed-section-card">
          <div className="ed-section-header">
            <span className="ed-section-title">
              <Calendar size={16} color="var(--indigo-500)" /> Upcoming Holidays
            </span>
            <button
              className="ed-text-btn"
              type="button"
              onClick={() => navigate(EMPLOYEE_WORKSPACE_ROUTES.holidays)}
            >
              View Calendar
            </button>
          </div>
          {upcoming.length === 0 ? (
            <div className="ed-empty">No upcoming holidays</div>
          ) : (
            <ul className="ed-holiday-list">
              {upcoming.map((h, i) => {
                const dateStr = h.holiday_date || h.date;
                const { day, month } = formatHolidayDate(dateStr);
                const weekday = formatHolidayWeekday(dateStr);
                return (
                  <li key={h.id || i} className="ed-holiday-item">
                    <div className="ed-holiday-date-box">
                      <span className="ed-holiday-day">{day}</span>
                      <span className="ed-holiday-month">{month}</span>
                    </div>
                    <div className="ed-holiday-body">
                      <div className="ed-holiday-name">{h.name || h.holiday_name}</div>
                      <div className="ed-holiday-weekday">{weekday}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
